import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { DataImportFileContainerInfo } from "../../../../../api/types/dataImportTypes";

interface Props {
  fileContainerInfo: DataImportFileContainerInfo;
  onClose: () => void;
  onDeleted: (id: string) => void;
}

const deleteDataImportFileContainer = withErrorHandling(adminApi.deleteDataImportFileContainer);

const DeleteFileContainerDialog = ({ fileContainerInfo, onClose, onDeleted }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [loading, setLoading] = useState(false);

  const handleDeleteClick = async () => {
    setLoading(true);
    const [resp, error] = await deleteDataImportFileContainer(fileContainerInfo.id);
    setLoading(false);

    if (error) {
      logError(error, "[DeleteFileContainerDialog]");
      sendNotificationError(error.message);
      return;
    }

    if (!resp.result) {
      sendNotification("File container cannot be deleted: it is used by a non-completed data import");
      return;
    }

    onDeleted(fileContainerInfo.id);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Delete</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Delete File Container?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete file container <strong>{fileContainerInfo.containerName}</strong>. This action
              is permanent and can&apos;t be undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={loading} onClick={handleDeleteClick}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteFileContainerDialog;
